import React, { useState, forwardRef, ReactNode, RefObject } from 'react';
import styled, { keyframes, css } from 'styled-components';
import colors from '../../common/theme/colors';
import constants, { fontWeight } from '../../common/theme/constants';

export interface KitTextFieldProps {
  children?: ReactNode;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLSelectElement | HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLSelectElement | HTMLInputElement, MouseEvent>) => void;
  onBlur?: (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => void;
  value?: string;
  label?: string;
  select?: boolean;
  id?: string;
  name: string;
  required?: boolean;
  error?: string;
  helpText?: string;
  type?: string;
  placeholder?: string;
  hideInfoErrorMessage?: boolean;
  appendedIcon?: ReactNode;
  disabled?: boolean;
  readOnly?: boolean | undefined;
}

const growOutFromMiddle = keyframes`
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1)
  }
`;

const getBorderColor = (error, disabled) => {
  if (disabled) {
    return colors.gray_40;
  }
  if (error) {
    return colors.red_50;
  }
  return colors.gray_90;
};

export const StyledInput = styled.input<{ focused: boolean; error?: string }>`
  font-family: ${({ theme }) => theme.font.fontFamily};
  border: none;
  color: ${({ theme, disabled }) =>
    disabled ? theme.input.disabled.text : theme.input.normal.text};
  font-weight: ${({ disabled }) => (disabled ? fontWeight.normal : fontWeight.bold)};
  width: 100%;
  && {
    margin-top: ${constants.spacing.absolute.xs}px;
    font-size: ${constants.fontSize.p}rem;
  }
  background-color: inherit;
  padding: 0 ${constants.spacing.absolute.s}px ${constants.spacing.absolute.xxs}px
    ${constants.spacing.absolute.s}px;
  &:focus {
    color: ${({ theme }) => theme.input.focus.text};
    outline: 0;
  }
`;

export const InputLabel = styled.label<{ focused: boolean; disabled: boolean; error?: string }>`
  font-family: ${({ theme }) => theme.font.fontFamily};
  font-size: ${constants.fontSize.text_14}rem;
  color: ${({ theme }) => theme.label};
  line-height: 1;
  & span {
    font-size: ${constants.fontSize.text_14}rem;
    margin-left: ${constants.spacing.absolute.s}px;
  }
  font-weight: ${({ focused }) => (focused ? fontWeight.bold : fontWeight.normal)};
  background-color: inherit;
  padding: ${constants.spacing.absolute.xs}px 0 0 0;
  && {
    margin-bottom: 0;
    display: block;
  }
  &:hover {
    font-weight: ${({ disabled }) => (disabled ? fontWeight.normal : fontWeight.bold)};
    color: ${({ theme, disabled }) => (disabled ? theme.label : theme.page.body.text)};
    input {
      color: ${({ theme, disabled }) => (disabled ? theme.label : theme.input.hover.text)};
    }
    select {
      color: ${({ theme, disabled }) => (disabled ? theme.label : theme.input.hover.text)};
    }
  }
  ${({ focused }) =>
    focused &&
    css`
      color: ${({ theme }) => theme.page.body.text};
      background-color: ${({ theme }) => theme.page.header.bg};
      box-shadow: 0 0 0 ${constants.border.width}px ${({ theme }) => theme.focus};
    `}
  ${({ error }) =>
    error &&
    css`
      color: ${({ theme }) => theme.page.body.text};
    `}
`;

export const StyledMessageP = styled.p<{ isError: boolean }>`
  color: ${({ isError, theme }) => (isError ? theme.error : theme.label)};
  font-size: ${constants.fontSize.text_12}rem;
  && {
    margin: ${constants.spacing.absolute.xs}px 0 0 ${constants.spacing.absolute.s}px;
  }
  line-height: 1;
  min-height: 1rem;
`;

const StyledSelect = styled.select<{ focused: boolean; error?: string }>`
  width: 100%;
  border: 0;
  margin: 0;
  appearance: none;
  min-width: 0;
  background: none;
  font-weight: ${({ disabled }) => (disabled ? fontWeight.normal : fontWeight.bold)};
  color: ${({ theme, disabled }) =>
    disabled ? theme.input.disabled.text : theme.input.normal.text};
  && {
    margin-top: ${constants.spacing.absolute.xs}px;
    font-size: ${constants.fontSize.p}rem;
    display: block;
  }
  background-color: inherit;
  padding: 0 ${constants.spacing.absolute.s}px ${constants.spacing.absolute.xxs}px
    ${constants.spacing.absolute.s}px;
  &:focus {
    color: ${({ theme }) => theme.input.focus.text};
    outline: 0;
  }
`;

const StyledSelectCustomArrow = styled.div`
  display: block;
  position: relative;
  width: 100%;
  &:after {
    content: '';
    width: 0.8rem;
    height: 0.8rem;
    background-color: ${colors.gray_50};
    clip-path: polygon(50% 85%, 100% 47%, 100% 62%, 50% 100%, 0 62%, 0 47%);
    position: absolute;
    right: 8px;
    top: 0.1rem;
  }
  & input {
    width: 100%;
  }
`;

const InnerWrapper = styled.div<{ focused: boolean; disabled: boolean; error?: string }>`
  width: 100%;
  &:after {
    left: 0;
    right: 0;
    bottom: 1px;
    position: relative;
    display: block;
    box-sizing: border-box;
    height: 2px;
    content: '';
    width: 100%;
    border-bottom: 1px solid ${({ error, disabled }) => getBorderColor(error, disabled)};
    ${({ focused, error }) =>
      (focused || error) &&
      css`
        animation: ${growOutFromMiddle} 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
        border-bottom: 2px solid ${error ? colors.red_50 : colors.gray_90};
      `}
  }
`;

const Wrapper = styled.div`
  width: 100%;
`;

const IconWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
`;

const IconContainer = styled.div`
  padding: 0 0 0 ${constants.spacing.absolute.s}px;
  margin: 0;
`;

/**
 * @deprecated - KitTextField is deprecated and has been replaced with KitInput and KitSelect
 */
const KitTextField = forwardRef(
  (props: KitTextFieldProps, ref: RefObject<HTMLSelectElement & HTMLInputElement>) => {
    const {
      children,
      label,
      select,
      required,
      id,
      error,
      helpText,
      hideInfoErrorMessage,
      appendedIcon,
      placeholder,
      disabled,
      readOnly,
      onBlur,
      ...otherProps
    } = props;
    const labelOutput = required && label ? `${label} * ` : label;
    const [focused, setFocused] = useState(false);

    let infoMessageId;
    if (id || labelOutput) {
      infoMessageId = id ? `${id}_info_message` : `${labelOutput.split(' ').join('')}_info_message`;
    }

    function getSelectElement() {
      return (
        <StyledSelectCustomArrow>
          <StyledSelect
            disabled={disabled || readOnly}
            focused={focused}
            ref={ref}
            onFocus={() => {
              setFocused(true);
            }}
            onBlur={(e) => {
              setFocused(false);
              if (typeof onBlur === 'function') {
                onBlur(e);
              }
            }}
            aria-required={required}
            aria-invalid={!!error}
            aria-describedby={infoMessageId}
            {...otherProps}
          >
            {children}
          </StyledSelect>
        </StyledSelectCustomArrow>
      );
    }

    function getInputElement() {
      return (
        <StyledInput
          ref={ref}
          focused={focused}
          disabled={disabled}
          readOnly={readOnly}
          onFocus={() => {
            setFocused(true);
          }}
          onBlur={(e) => {
            setFocused(false);
            if (typeof onBlur === 'function') {
              onBlur(e);
            }
          }}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={infoMessageId}
          placeholder={placeholder}
          {...otherProps}
        />
      );
    }

    function getInfoMessage() {
      let errorMode = false;

      let message = '';
      if (helpText) {
        message = helpText;
      }

      if (error) {
        // NOTE: If there's an error that always takes priority over helpText.
        errorMode = true;
        message = error;
      }

      return (
        <StyledMessageP isError={errorMode} id={infoMessageId}>
          {message}
        </StyledMessageP>
      );
    }

    return (
      <Wrapper>
        <InnerWrapper focused={focused} error={error} disabled={disabled || readOnly}>
          <InputLabel htmlFor={id} focused={focused} disabled={disabled || readOnly} error={error}>
            {label && <span>{labelOutput}</span>}
            <IconWrapper>
              {appendedIcon && <IconContainer>{appendedIcon}</IconContainer>}
              {select ? getSelectElement() : getInputElement()}
            </IconWrapper>
          </InputLabel>
        </InnerWrapper>
        {!hideInfoErrorMessage && getInfoMessage()}
      </Wrapper>
    );
  }
);

export default KitTextField;
